class CyclingImages {
  constructor(element) {
    if (element.cyclingImages) return element.cyclingImages;

    this.container = element;
    this.container.cyclingImages = this;
    this.items = Array.from(element.querySelectorAll('.cycling-images__item'));
    this.media = window.matchMedia ? window.matchMedia('(min-width: 750px)') : { matches: false };
    this.currentIndex = 0;
    this.interval = Math.max(Number(element.dataset.cycleInterval) || 20000, 1000);
    this.autoPlay = element.dataset.autoCycle === 'true';
    this.timer = null;

    this.onVisibilityChange = this.handleVisibilityChange.bind(this);
    this.onMediaChange = this.handleMediaChange.bind(this);

    if (this.items.length > 0) this.init();
  }

  get visibleCount() {
    return Math.min(this.media.matches ? 2 : 1, this.items.length);
  }

  get targetItems() {
    return Array.from({ length: this.visibleCount }, (_, offset) =>
      this.items[(this.currentIndex + offset) % this.items.length]
    );
  }

  init() {
    this.applyState(this.targetItems);
    this.container.dataset.cyclingReady = 'true';
    if (this.media.matches) this.preloadNext();
    this.start();

    document.addEventListener('visibilitychange', this.onVisibilityChange);

    if (this.media.addEventListener) {
      this.media.addEventListener('change', this.onMediaChange);
    } else if (this.media.addListener) {
      this.media.addListener(this.onMediaChange);
    }
  }

  applyState(targets) {
    const visible = new Set(targets);

    this.items.forEach((item) => {
      const isVisible = visible.has(item);
      item.classList.toggle('cycling-images__item--visible', isVisible);
      item.setAttribute('aria-hidden', String(!isVisible));

      if ('inert' in item) {
        item.inert = !isVisible;
      }
    });
  }

  show(startIndex) {
    this.currentIndex = ((startIndex % this.items.length) + this.items.length) % this.items.length;
    this.applyState(this.targetItems);
    this.preloadNext();
  }

  preloadNext() {
    const nextIndex = (this.currentIndex + this.visibleCount) % this.items.length;

    for (let offset = 0; offset < this.visibleCount; offset += 1) {
      const item = this.items[(nextIndex + offset) % this.items.length];
      const image = item ? item.querySelector('img') : null;
      if (!image) continue;

      image.loading = 'eager';
      if (typeof image.decode === 'function') {
        image.decode().catch(function () {});
      }
    }
  }

  next() {
    this.show(this.currentIndex + this.visibleCount);
  }

  isInViewport() {
    const bounds = this.container.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    return bounds.bottom > 0 && bounds.top < viewportHeight;
  }

  start() {
    this.pause();
    if (!this.media.matches || !this.autoPlay || this.items.length <= this.visibleCount || document.hidden) return;

    this.timer = window.setInterval(() => {
      if (!this.isInViewport() || this.container.contains(document.activeElement)) return;
      this.next();
    }, this.interval);
  }

  pause() {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  }

  handleVisibilityChange() {
    if (document.hidden) this.pause();
    else this.start();
  }

  handleMediaChange() {
    this.currentIndex = 0;
    this.applyState(this.targetItems);
    if (this.media.matches) this.preloadNext();
    this.start();
  }

  destroy() {
    this.pause();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);

    if (this.media.removeEventListener) {
      this.media.removeEventListener('change', this.onMediaChange);
    } else if (this.media.removeListener) {
      this.media.removeListener(this.onMediaChange);
    }

    delete this.container.cyclingImages;
  }
}

function initCyclingImages(scope) {
  const root = scope || document;
  root.querySelectorAll('.cycling-images').forEach((container) => new CyclingImages(container));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function () { initCyclingImages(document); });
} else {
  initCyclingImages(document);
}

document.addEventListener('shopify:section:load', function (event) {
  initCyclingImages(event.target);
});

document.addEventListener('shopify:section:unload', function (event) {
  event.target.querySelectorAll('.cycling-images').forEach(function (container) {
    if (container.cyclingImages) container.cyclingImages.destroy();
  });
});
